import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Delete, Fingerprint, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAppLock } from "@/contexts/AppLockContext";
import { unlockBiometric } from "@/lib/webauthn";

export default function AppLockOverlay() {
  const { user, logout } = useAuth();
  const { status, locked, unlock } = useAppLock();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const autoTried = useRef(false);
  const abortRef = useRef(null);

  const hasBiometric = (status?.biometric_count || 0) > 0;
  const expectedLen = status?.pin_len || 6;

  useEffect(() => {
    if (!locked) {
      setPin("");
      setError("");
      autoTried.current = false;
      try { abortRef.current?.abort(); } catch { /* ignore */ }
      abortRef.current = null;
    }
  }, [locked]);

  // Abort any in-flight Face ID prompt if the overlay unmounts.
  useEffect(() => () => { try { abortRef.current?.abort(); } catch { /* ignore */ } }, []);

  // "Forgot passcode": clear the lock server-side, then force a fresh Google sign-in.
  // Re-entry still requires Google auth, so data stays protected — but the old code is gone.
  const resetAndLogout = useCallback(async () => {
    setBusy(true);
    try { await api.disableAppLock(); } catch { /* ignore */ }
    try { localStorage.setItem("sd_set_new_passcode", "1"); } catch { /* ignore */ }
    logout();
  }, [logout]);

  const handleLockout = useCallback(() => {
    toast.error("Too many attempts — sign in with Google to reset your passcode.");
    resetAndLogout();
  }, [resetAndLogout]);

  const submitPin = useCallback(
    async (code) => {
      setBusy(true);
      setError("");
      try {
        await api.unlockAppPin(code);
        unlock();
      } catch (e) {
        const s = e.response?.status;
        if (s === 423) return handleLockout();
        setPin("");
        setShake(true);
        setTimeout(() => setShake(false), 500);
        const detail = e.response?.data?.detail;
        // Only trust a clean 400 with a JSON string detail as a "wrong passcode" message.
        // Anything else (5xx/520 origin error, HTML body, or no response) gets a friendly,
        // non-scary message instead of raw Cloudflare/server text.
        if (s === 400 && typeof detail === "string") setError(detail);
        else if (!e.response) setError("Can't reach the server — check your connection and try again.");
        else if (s >= 500) setError("The server is having a moment. Please try again.");
        else setError("Incorrect passcode.");
      } finally {
        setBusy(false);
      }
    },
    [unlock, handleLockout]
  );

  // auto = the automatic on-open attempt; failures stay quiet so the keypad + button remain usable.
  const tryBiometric = useCallback(
    async (auto = false) => {
      setBusy(true);
      if (!auto) setError("");
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        await unlockBiometric(controller.signal);
        unlock();
      } catch (e) {
        if (e?.name === "AbortError") return;
        const s = e.response?.status;
        if (s === 423) return handleLockout();
        // On the silent auto-attempt, iOS may block Face ID without a tap (NotAllowedError) —
        // don't show a scary error; the user can tap the button. Only surface manual failures.
        if (!auto) {
          if (e.response && e.response.status >= 500) setError("The server is having a moment. Please try again.");
          else if (e.response === undefined && e?.name && e.name !== "NotAllowedError" && e.name !== "AbortError" && !e?.response) {
            // Distinguish a network failure to our server from a WebAuthn/device error.
            setError(e?.isAxiosError ? "Can't reach the server — check your connection and try again." : "Biometric unlock didn't work — enter your passcode.");
          } else setError("Biometric unlock didn't work — enter your passcode.");
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setBusy(false);
      }
    },
    [unlock, handleLockout]
  );

  // When the lock screen appears and Face ID is set up, start the scan automatically (one attempt).
  useEffect(() => {
    if (locked && hasBiometric && !autoTried.current) {
      autoTried.current = true;
      tryBiometric(true);
    }
  }, [locked, hasBiometric, tryBiometric]);

  const press = (d) => {
    if (busy || pin.length >= expectedLen) return;
    const next = pin + d;
    setPin(next);
    if (next.length === expectedLen) submitPin(next);
  };
  const back = () => setPin((p) => p.slice(0, -1));

  if (!user || !locked || !status?.enabled) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-zinc-950 px-6" data-testid="app-lock-overlay">
      <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "radial-gradient(120% 80% at 50% 0%, rgba(155,34,38,0.30), transparent 60%), radial-gradient(110% 75% at 50% 100%, rgba(224,87,91,0.22), transparent 55%)" }} />
      <div className="relative flex w-full max-w-xs flex-col items-center">
        <div className="mb-5 h-16 w-16 overflow-hidden rounded-2xl shadow-lg ring-1 ring-white/10">
          <img src="/icon-512.png" alt="Stage Door" draggable={false} className="h-full w-full select-none object-cover" data-testid="app-lock-logo" />
        </div>
        <h1 className="font-display text-xl font-800 tracking-tight text-zinc-50">Stage Door is locked</h1>
        <p className="mb-6 mt-1 text-sm text-zinc-400">Enter your passcode to continue</p>

        <div className={`mb-2 flex items-center gap-3 ${shake ? "animate-[shake_0.4s]" : ""}`} data-testid="pin-dots">
          {Array.from({ length: expectedLen }).map((_, i) => (
            <span key={i} className={`h-3.5 w-3.5 rounded-full border transition-colors ${i < pin.length ? "border-[#E0575B] bg-[#E0575B]" : "border-zinc-700 bg-transparent"}`} />
          ))}
        </div>
        <div className="mb-4 h-5 text-center text-xs text-red-400" data-testid="app-lock-error">{error}</div>

        <div className="grid w-full grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button key={n} onClick={() => press(String(n))} disabled={busy} data-testid={`pin-key-${n}`}
              className="flex h-16 items-center justify-center rounded-2xl bg-zinc-900 text-2xl font-600 text-zinc-100 transition-colors hover:bg-zinc-800 active:bg-zinc-700 disabled:opacity-50">
              {n}
            </button>
          ))}
          <div aria-hidden="true" data-testid="pin-key-spacer" />
          <button onClick={() => press("0")} disabled={busy} data-testid="pin-key-0"
            className="flex h-16 items-center justify-center rounded-2xl bg-zinc-900 text-2xl font-600 text-zinc-100 transition-colors hover:bg-zinc-800 active:bg-zinc-700 disabled:opacity-50">
            0
          </button>
          <button onClick={back} disabled={busy || !pin.length} data-testid="pin-key-back"
            className="flex h-16 items-center justify-center rounded-2xl text-zinc-300 transition-colors hover:bg-zinc-900 disabled:opacity-30">
            <Delete className="h-6 w-6" />
          </button>
        </div>

        {hasBiometric && (
          <button onClick={() => tryBiometric(false)} disabled={busy} data-testid="app-lock-biometric-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#9B2226]/15 px-4 py-2 text-sm font-600 text-[#E0575B] transition-colors hover:bg-[#9B2226]/25 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} {busy ? "Scanning…" : "Use Face ID / Touch ID"}
          </button>
        )}
        <button onClick={resetAndLogout} disabled={busy} className="mt-5 text-xs text-zinc-500 hover:text-zinc-300 disabled:opacity-50" data-testid="app-lock-logout-btn">
          Forgot passcode? Sign in with Google
        </button>
      </div>
    </div>
  );
}
